import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Row, Col, Switch } from 'antd';
import { useLeadContext } from '@contexts';
import HeadingTypography from 'src/app/components/shared/Typography/Heading';
import { getFutureDate, IProduct } from '@rasayi-workspace/shared';
import { debounce } from 'lodash';
import AlertBar from 'src/app/components/shared/Alert';

const { Item } = Form;
const { Option } = Select;

interface ProductDetailsTabProps {
    onClose: () => void;
    onSaveProductValues: (data: any) => void;
    saveData: (data: any) => void;
    initialValue: IProduct[];
    isLoading: boolean;
    exsistingProductLength: number;
    error: { type: 'error' | 'success' | 'info' | 'warning' | ''; message: string };
    setError: (data: { type: 'error' | 'success' | 'info' | 'warning' | ''; message: string }) => void;
}

export const ProductDetailsTab: React.FC<ProductDetailsTabProps> = ({ onClose, onSaveProductValues, saveData, initialValue, isLoading, exsistingProductLength = 0, error, setError }) => {
    const { brands, productType, installmentPlans, aprs } = useLeadContext();
    const [form] = Form.useForm();

    const [products, setProducts] = useState<IProduct[]>([
        {
            product_type_id: '',
            quantity: '',
            brand_id: '',
            product_name: '',
            product_description: '',
            locking_charges: '',
            delivery_charges: false,
            service_charges: false,
            down_payment: '',
            token_amount: '',
            apr: '',
            retail_price: '',
            instalment_plan_id: '',
            monthlyInstallment: '',
            lastInstallment: '',
            total_price: '',
        },
    ]);

    useEffect(() => {
        if (initialValue.length) form.setFieldsValue({ products: initialValue });
    }, [initialValue]);

    const useCalculateUpdatedProducts = debounce((values, changedField, callback) => {
        let result = {};
        result = values.products.map((product: any) => {
            if (!product) return {};
            const updatedProduct = { ...product };

            // Update Locking Charges based on the selected Brand
            if (product.brand_id) {
                const selectedBrand = brands.find((b: any) => b.id === product.brand_id);
                if (selectedBrand) {
                    updatedProduct.locking_charges = selectedBrand.charges || 0;
                }
            }
            if (product.instalment_plan_id) {
                const selectedInstallment = installmentPlans.find((plan: any) => product.instalment_plan_id === plan.id);
                if (selectedInstallment) {
                    updatedProduct.lastInstallment = getFutureDate(selectedInstallment.cycle);
                }
            }

            // Update APR based on Retail Price
            if (product.retail_price) {
                const applicableApr = aprs.filter((apr: any) => product.retail_price > apr.amount).sort((a: any, b: any) => b.amount - a.amount)[0];
                updatedProduct.apr = applicableApr ? applicableApr.rate : '';
            }
            if (changedField === 'apr') {
                updatedProduct.apr = product.apr;
            }

            if (product.instalment_plan_id && updatedProduct.retail_price && updatedProduct.apr) {
                const retail_price = Number(updatedProduct.retail_price) ?? 0;
                const selectedInstallment = installmentPlans.find((plan: any) => product.instalment_plan_id === plan.id);

                if (selectedInstallment?.cycle) {
                    const aprPercentage = updatedProduct.apr * selectedInstallment.cycle;

                    const aprAmount = retail_price * (aprPercentage / 100);

                    let totalLoanAmount = Number(aprAmount + retail_price);

                    if (product.serviceCharges) {
                        totalLoanAmount += 400;
                    }
                    if (product.deliveryCharges) {
                        totalLoanAmount += 600;
                    }
                    if (product.locking_charges) {
                        totalLoanAmount += Number(product.locking_charges) ?? 0;
                    }

                    const montlyInstallment = totalLoanAmount / selectedInstallment.cycle;

                    updatedProduct.monthlyInstallment = Math.ceil(montlyInstallment);

                    let totalPrice = totalLoanAmount;
                    if (Number(product.downpayment)) {
                        totalPrice += Number(product.downpayment);
                    }
                    if (Number(product.tokenAmount)) {
                        totalPrice += Number(product.tokenAmount);
                    }
                    updatedProduct.total_price = totalPrice;
                }
            }
            return updatedProduct;
        });
        if (callback) callback(result);
    }, 1000);

    const handleValuesChanges = (changedValues: any, values: any) => {
        const changedField = Object.keys(changedValues?.products?.[0] || {})[0];

        useCalculateUpdatedProducts(values, changedField, (updatedProducts: any) => {
            form.setFieldsValue({ products: updatedProducts });
            // Save updated data
            saveData(updatedProducts);
        });
    };

    const items = [
        {
            label: 'Product Type',
            name: 'product_type_id',
            type: 'select',
            required: true,
            options: productType.map(obj => {
                return { label: obj.name, value: obj.id };
            }),
        },
        {
            label: 'Quantity',
            name: 'quantity',
            type: 'number',
            required: true,
        },
        {
            label: 'Brand',
            name: 'brand_id',
            type: 'select',
            required: true,
            options: brands.map(obj => {
                return { label: obj.name, value: obj.id };
            }),
        },
        {
            label: 'Product Name',
            name: 'product_name',
            type: 'input',
            required: true,
        },
        {
            label: 'Product Description',
            name: 'product_description',
            type: 'input',
            required: true,
        },
        {
            label: 'Locking Charges',
            name: 'locking_charges',
            disabled: true,
            readOnly: true,
        },
        {
            label: 'Delivery Charges',
            name: 'delivery_charges',
            valuePropName: 'checked',
            type: 'switch',
        },
        {
            label: 'Service Charges',
            name: 'service_charges',
            valuePropName: 'checked',
            type: 'switch',
        },
        {
            label: 'Down Payment',
            name: 'down_payment',
            type: 'input',
            rules: [{ pattern: /^[0-9]+$/, message: 'Down Payment is not a number' }],
        },
        {
            label: 'Token Amount',
            name: 'token_amount',
            type: 'input',
            rules: [{ pattern: /^[0-9]+$/, message: 'Token Amount is not a number' }],
        },
        {
            label: 'Apr',
            name: 'apr',
            required: true,
            rules: [{ pattern: /^[0-9]+$/, message: 'Apr is not a number' }],
        },
        {
            label: 'Retail Price',
            name: 'retail_price',
            required: true,
            rules: [{ pattern: /^[0-9]+$/, message: 'Retail Price is not a number' }],
        },
        {
            label: 'Installment Plan',
            name: 'instalment_plan_id',
            type: 'select',
            required: true,
            options: installmentPlans.map(obj => {
                return { label: obj.name, value: obj.id };
            }),
        },
        {
            label: 'Monthly Installment',
            name: 'monthlyInstallment',
            disabled: true,
            type: 'input',
        },
        {
            label: 'Last Installment',
            name: 'lastInstallment',
            disabled: true,
            type: 'input',
        },
        {
            label: 'Total Price',
            name: 'total_price',
            disabled: true,
            type: 'input',
        },
    ];

    return (
        <>
            <AlertBar error={error} setError={setError} />

          <Form
            key={products.length}
            form={form}
            layout="vertical"
            onValuesChange={(_, values) => {
              handleValuesChanges(_, values);
            }}
            style={{
              maxHeight: '700px',
              overflow: 'hidden',
            }}

          >
            <Form.List name="products" initialValue={products}>
              {(fields, { add, remove }) => (
                <>

                  <div
                    style={{
                      maxHeight: '620px',
                      overflowY: 'auto',
                      paddingRight: '8px',
                    }}
                  >
                    {fields.map(({ key, name, ...restField }, index) => (
                      <div key={key} className="pb-[20px]">
                        <HeadingTypography fontSize={16} classNames="flex items-center h-[26px] font-medium">
                          Product ({index + 1})
                        </HeadingTypography>
                        <Row gutter={[12, 0]} className="mt-2" style={{ marginBottom: '0%' }}>
                          {items.map(({ label, name: fieldName, type, required, options, valuePropName, disabled, rules }) => (
                            <Col span={8} key={`${name}-${fieldName}`} style={{ marginTop: '0px' }}>
                              <Item
                                {...restField}
                                label={
                                  <HeadingTypography fontSize={14} classNames="flex items-center h-[26px] font-normal">
                                    {label} {required && <span className="text-[#BD273B]"> *</span>}
                                  </HeadingTypography>
                                }
                                name={[name, fieldName]}
                                valuePropName={valuePropName}
                                rules={rules}
                                validateTrigger={['onSubmit', 'onChange', 'onBlur']}
                              >
                                {type === 'select' ? (
                                  <Select
                                    showSearch
                                    className="lead-detail-selector w-full"
                                    allowClear
                                    placeholder={`Select ${label}`}
                                    optionFilterProp="label"
                                    filterSort={(optionA, optionB) =>
                                      (optionA?.label ?? '')
                                        .toString()
                                        .toLowerCase()
                                        .localeCompare((optionB?.label ?? '').toString().toLowerCase())
                                    }
                                    options={options}
                                  />
                                ) : type === 'switch' ? (
                                  <Switch className="custom-switch" />
                                ) : (
                                  <Input
                                    disabled={disabled}
                                    style={{ height: '35px' }}
                                    placeholder={label}
                                    type={type ?? 'text'}
                                  />
                                )}
                              </Item>
                            </Col>
                          ))}
                        </Row>
                        {fields.length > 1 && (
                          <Button disabled={index < exsistingProductLength} danger onClick={() => remove(name)}>
                            Remove Product
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Static buttons section */}
                  <div>
                    <div className="flex justify-between items-center">
                      <Button
                        htmlType="button"
                        onClick={() => add()}
                        className="text-red-700 border-red-700 bg-white hover:bg-red-700 hover:text-white transition-colors"
                      >
                        + Add Product Details
                      </Button>

                      <div className="flex">
                        <Button
                          onClick={onClose}
                          className="mr-2 text-red-700 border-red-700 bg-white hover:bg-red-700 !hover:text-white !hover:border-white transition-colors"
                        >
                          Cancel
                        </Button>

                        <Button
                          loading={isLoading}
                          onClick={onSaveProductValues}
                          className="bg-red-700 text-white border-red-700 hover:bg-red-800 hover:border-red-800 hover:text-white !text-white transition-colors"
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Form.List>
          </Form>
        </>
    );
};
